// DemoCodeClickChart.js
import { useState, useEffect } from "react"
import { SwmmOutChartjs } from '../Charting_Components/SwmmOutChartjs'
import { SwmmOut } from "@fileops/swmm-node"
import { UniversalDropDown } from "@fileops/swmm-react"
import { Typography, Button } from '@mui/material'
import InputForm from './InputForm'
import "../../css/DemoCode.css"

/**
 * DemoCodeClickChart
 * 
 * @param {JSON} model A state-controlled SWMM model in JSON format.
 * @param {function} setModel state-controlled update function for updating 'model'.
 * @param {string} targetType string of type 'SUBCATCHMENTS', 'CONDUITS', 'JUNCTIONS', etc.
 * @param {SwmmOut} swmmOut the contents of a binary .out file in a SwmmOut object.
 * @param {string} target the name or 'id' of the subcatchment, link, node, etc.
 * @param {function} setNewName function for setting a new name on a name change.
 * @param {Object} dialogStates object that describes the states of the dialogs in the swmmReact UI.
 * @returns UI for interactive swmm modeling.
 */
export default function DemoCodeClickChart({model, setModel, targetType, swmmOut, target, setNewName, dialogStates}) {
  const [targetParameter, settargetParameter] = useState(undefined)
  const [targetParameterVal, settargetParameterVal] = useState(undefined)
  const [resultArrayName, setResultArrayName] = useState('')
  const [newTargetName, setNewTargetName] = useState(target)
  const [submodel, setSubmodel] = useState(
    targetType==='SUBCATCHMENTS'
    ?{
      [targetType] : {[target]:JSON.parse(JSON.stringify(model[targetType][target]))},
      SUBAREAS     : {[target]:JSON.parse(JSON.stringify(model.SUBAREAS[target]))}
    }
    :targetType==='CONDUITS'
    ?{
      [targetType] : {[target]:JSON.parse(JSON.stringify(model[targetType][target]))},
      XSECTIONS    : {[target]:JSON.parse(JSON.stringify(model.XSECTIONS[target]))},
      LOSSES       : {[target]:JSON.parse(JSON.stringify(model.LOSSES[target]))}
    }
    :targetType==='ORIFICES'
    ?{
      [targetType] : {[target]:JSON.parse(JSON.stringify(model[targetType][target]))},
      XSECTIONS    : {[target]:JSON.parse(JSON.stringify(model.XSECTIONS[target]))}
    }
    :targetType==='WEIRS'
    ?{
      [targetType] : {[target]:JSON.parse(JSON.stringify(model[targetType][target]))},
      XSECTIONS    : {[target]:JSON.parse(JSON.stringify(model.XSECTIONS[target]))}
    }
    :{[targetType] : {[target]:JSON.parse(JSON.stringify(model?.[targetType]?.[target]?model[targetType][target]:{}))}}
  )
  const [out_data,  setout_data] = useState({})
  
  // Model data to be modified by swmmReact interface options.
  useEffect(() =>{
    if(model){
      let sectionKeys = []
      // Sections that need more complex parsing than provided with this algorithm.
      let nonSections = ['TITLE', 'MAP', 'OPTIONS', 'TIMESERIES', 'HYDROGRAPHS', 'EVAPORATION', 'BUILDUP', 'WASHOFF', 'COVERAGES', 'REPORT', 'COORDINATES', 'VERTICES', 'POLYGONS', 'SYMBOLS', 'CURVES', 'FILES', 'LID_CONTROLS', 'LID_USAGE', 'LABELS', 'TAGS', 'PROFILES', 'LOADINGS']
      Object.keys(model).forEach(k=>{
        if(Object.keys(model[k]).length > 0 && !(nonSections.includes(k))){
          sectionKeys.push(k)
        }
      })
    }

    let losses = {}
    // If there is no entry in losses for the conduit, create one in the submodel
    if(model?.['LOSSES']?.[target] == null){
      losses = {[target] : {Kin:0, Kout:0, Kavg:0, Flap:'NO', Seepage:0}}
    } else {
      losses = {[target] : model['LOSSES'][target]}
    }

    if(targetType==='SUBCATCHMENTS'){
      setSubmodel({
        [targetType] : {[target]:JSON.parse(JSON.stringify(model[targetType][target]))},
        'SUBAREAS'   : {[target]:JSON.parse(JSON.stringify(model['SUBAREAS'][target]))}
      })
    } else if(['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE'].includes(targetType) && model?.[targetType]?.[target] != null){
      setSubmodel({[targetType]:{[target]:JSON.parse(JSON.stringify(model[targetType][target]))}})

    } else if(['CONDUITS', 'PUMPS', 'ORIFICES', 'WEIRS', 'OUTLETS'].includes(targetType)){
      if(targetType === 'CONDUITS'){
        setSubmodel(
          {
            [targetType]:{[target]:JSON.parse(JSON.stringify(model[targetType][target]))},
            XSECTIONS:{[target]:JSON.parse(JSON.stringify(model.XSECTIONS[target]))},
            LOSSES:losses,
          }
        )
      } else if(['ORIFICES', 'WEIRS'].includes(targetType)){
        setSubmodel(
          {
            [targetType]:{[target]:JSON.parse(JSON.stringify(model[targetType][target]))},
            XSECTIONS:{[target]:JSON.parse(JSON.stringify(model.XSECTIONS[target]))},
          }
        )
      } else {
        setSubmodel({[targetType]:{[target]:JSON.parse(JSON.stringify(model[targetType][target]))}})
      }
    }
  }, [target, targetType, model])

  /**
   * Parse the array buffer if there is data from a model run
   */
  useEffect(()=>{
    if(swmmOut != null){
      if(targetParameter >= 0 && targetParameterVal >= 0){
        setout_data(swmmOut.processOut([target], targetParameterVal, targetParameter))
      }
    }
  }, [swmmOut, targetParameter, target, targetParameterVal])

  const saveEdits = () => {
    if(targetType === 'SUBCATCHMENTS'){
      setModel(prev => ({
        ...prev, [targetType]: {
          ...prev[targetType], [target]:
            submodel[targetType][target]
        },
        SUBAREAS:{
          ...prev.SUBAREAS, [target]:
            submodel.SUBAREAS[target]
        }
      }))
    } else if(['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE', 'RAINGAGES'].includes(targetType)){
      setModel(prev => ({
        ...prev, [targetType]: {
          ...prev[targetType], [target]:
            submodel[targetType][target]
        }
      }))
    } else if(['CONDUITS'].includes(targetType)){
      let validSecs = ['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE']
      let Node1 = submodel[targetType][target].Node1
      let Node2 = submodel[targetType][target].Node2
      // If Node1 === Node2, do not update
      if(Node1 === Node2){
        alert('Upstream node and downstream node cannot be the same node.')
        return;
      }
      // Do not update if Node1 is not in  ['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE']
      let validUpstreamNode = false
      validSecs.forEach((v)=>{
        if(Object.keys(model[v]).includes(Node1)) validUpstreamNode = true
      })
      // Do not update if Node2 is not in  ['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE']
      let validDownstreamNode = false
      validSecs.forEach((v)=>{
        if(Object.keys(model[v]).includes(Node2)) validDownstreamNode = true
      })

      if(!validDownstreamNode || !validUpstreamNode){
        alert('Upstream node and downstream node must exist.')
        return
      }

      setModel(prev => ({
        ...prev, [targetType]: {
          ...prev[targetType], [target]:
            submodel[targetType][target]
        },
        LOSSES:{
          ...prev.LOSSES, [target]:
            submodel.LOSSES[target]
        },
        XSECTIONS:{
          ...prev.XSECTIONS, [target]:
            submodel.XSECTIONS[target]
        }
      }))
    } else if(['ORIFICES', 'WEIRS'].includes(targetType)){
      let validSecs = ['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE']
      // If Node1 === Node2, do not update
      if(submodel[targetType][target].Node1 === submodel[targetType][target].Node2){
        return;
      }
      // Do not update if Node1 is not in  ['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE']
      let validUpstreamNode = false
      validSecs.forEach((v)=>{
        if(Object.keys(model[v]).includes(submodel[targetType][target].Node1)) validUpstreamNode = true
      })
      // Do not update if Node2 is not in  ['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE']
      let validDownstreamNode = false
      validSecs.forEach((v)=>{
        if(Object.keys(model[v]).includes(submodel[targetType][target].Node1)) validDownstreamNode = true
      })

      if(!validDownstreamNode || !validUpstreamNode){
        return
      }

      setModel(prev => ({
        ...prev, [targetType]: {
          ...prev[targetType], [target]:
            submodel[targetType][target]
        },
        XSECTIONS:{
          ...prev.XSECTIONS, [target]:
            submodel.XSECTIONS[target]
        }
      }))
    } else if(['PUMPS', 'OUTLETS'].includes(targetType)){
      let validSecs = ['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE']
      // If Node1 === Node2, do not update
      if(submodel[targetType][target].Node1 === submodel[targetType][target].Node2){
        return;
      }
      // Do not update if Node1 is not in  ['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE']
      let validUpstreamNode = false
      validSecs.forEach((v)=>{
        if(Object.keys(model[v]).includes(submodel[targetType][target].Node1)) validUpstreamNode = true
      })
      // Do not update if Node2 is not in  ['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE']
      let validDownstreamNode = false
      validSecs.forEach((v)=>{
        if(Object.keys(model[v]).includes(submodel[targetType][target].Node1)) validDownstreamNode = true
      })

      if(!validDownstreamNode || !validUpstreamNode){
        return
      }

      setModel(prev => ({
        ...prev, [targetType]: {
          ...prev[targetType], [target]:
            submodel[targetType][target]
        }
      }))
    }
  }

  /**
   * Dropdown menu data population. This could be a target for cleanup.
   */
  useEffect(()=>{
    if(['JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE'].includes(targetType)){
      setResultArrayName('NODE_RESULTS')
      settargetParameterVal(SwmmOut.NODE)
    } else if (['CONDUITS', 'PUMPS', 'ORIFICES', 'WEIRS', 'OUTLETS'].includes(targetType)){
      setResultArrayName('LINK_RESULTS')
      settargetParameterVal(SwmmOut.LINK)
    } else if (['SUBCATCHMENTS'].includes(targetType)){
      setResultArrayName('SUBCATCHMENT_RESULTS')
      settargetParameterVal(SwmmOut.SUBCATCH)
    }
  }, [targetType])

  function handleChange(e) {
    setNewName(e.target.value)
    setNewTargetName(e.target.value)
  }

  /**
   * If there is a valid model, create a UI for the model.
   */
  if(['CONDUITS', 'JUNCTIONS', 'OUTFALLS', 'DIVIDERS', 'STORAGE', 'SUBCATCHMENTS', 'PUMPS', 'ORIFICES', 'WEIRS', 'OUTLETS', 'RAINGAGES'].includes(targetType) && submodel[targetType][target] && model?.[targetType]?.[target] != null){
    return(
      <>
      {
        <>
          <Typography id="modal-modal-title" variant="p4" >
            <div style={{display:"flex", flexDirection: "column"}}>
              <label>{targetType} ID:</label>
              <input style={{fontSize: '16px', background: 'rgb(50, 50, 50)', color: 'lightskyblue', border: 'none', textAlign: 'center'}} type="text" value={newTargetName} onChange={handleChange} />
            </div>
          </Typography>
          <div className="controlcol" >  
            {
              <>
              <div className="controlrow" style={{background:"black"}}>
                {
                  resultArrayName && 
                  <UniversalDropDown 
                    IDs={SwmmOut[resultArrayName]} 
                    selectText={"Select an Output Parameter"} 
                    value={SwmmOut[resultArrayName][0]}
                    onChange={(val)=>settargetParameter(SwmmOut[resultArrayName].indexOf(val)===-1?0:SwmmOut[resultArrayName].indexOf(val))} >
                  </UniversalDropDown>
                }
                </div>
              </>
            }
          </div>
          <div style={{height: "calc(100% - 410px)", background:'black'}}>
            {
              <SwmmOutChartjs 
                out_data={out_data} 
                targets={[target]} 
                titleText={swmmOut&&resultArrayName&&targetParameter>=0?swmmOut.unitString(resultArrayName, targetParameter):''}
                />
            }
          </div>
          <div>
            <Button fullWidth onClick={saveEdits} >Save Edits</Button>
          </div>
          <div style={{height: "280px", background:'black', overflowY: 'scroll'}}>
            {
              targetType && 
              targetType in model && 
              target &&
              target in model[targetType] &&
              <>
              <div className="infocol">
                <InputForm model={submodel} setModel={setSubmodel} fullModel={model} setFullModel={setModel} targetType={targetType} target={target} dialogStates={dialogStates} />
              </div>
              </>
            }
          </div>
          </>
        }
      </>
    )
  } else {
    return (
    <>
      <pre>{JSON.stringify(model, null, 2)}</pre>
      <div>{targetType}</div>
      <div>{target}</div>
    </>
    )
  }
}